import { isArray } from 'lodash';
import { Api } from '../api';
import {
  CreateIssueThread,
  SearchThreadsResponse,
  Thread,
  UpdateThreadStateResponse,
  CreateCustomerThreadResonse,
  CreateCustomerThreadRequest,
  CreateThread,
  IssueStatus,
  ThreadFiltersRequest,
  ThreadFilterResponse,
  ThreadsListRequest,
  ThreadsListResponse,
  AddMessageReactionResponse,
  AddMessageReactionRequest,
  UpdateThreadLastVisitRequest,
  ThreadLastVisitResponseData,
  ThreadListChannel,
  CreateGroupResponse,
  CreateGroupRequest,
  UpdateGroupMembersRequest,
  UpdateGroupMembersResponse,
} from './model';
import {
  updateCurrentChannel,
  updateThreadTabNavigationValue,
  upsertChannel,
} from '../channel/slice';
import { ChannelTypeEnum } from '../channel/model';
import { isWeb } from '@zonofi/common';
import {
  getMobileAppliedFilterByScreen,
  updateMobileFiltersByScreen,
} from '../filter/slice';
import { MobileSiftScreen, SectionFilterKeys } from '../filter/model';
import { getThreadFilterItems, handleStatusFilter } from './helper';

export const threadApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    createIssueThread: builder.mutation<Thread | Thread[], CreateIssueThread>({
      query(arg) {
        return {
          url: `hub/communication-v2/api/threads/issue`,
          method: 'POST',
          body: arg,
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then((response) => {
            const { data } = response;
            let thread: Thread;
            if (isArray(data)) {
              thread = data[0];
            } else {
              thread = data;
            }
            const { id, channelName } = thread;
            dispatch(
              upsertChannel({
                [id]: {
                  ...thread,
                  isActive: true,
                  type: ChannelTypeEnum.ISSUETHREAD,
                  latestMessage: channelName,
                  latestMessageTimeToken: Date.now() + '0000',
                },
              })
            );
            if (isWeb()) {
              dispatch(
                updateThreadTabNavigationValue(ChannelTypeEnum.ISSUETHREAD)
              );
              dispatch(updateCurrentChannel(id));
            }
          })
          .catch((error) => console.log(error));
      },
    }),
    searchThreads: builder.query<
      SearchThreadsResponse,
      {
        searchKey: string;
        sellerWorkspaceId: string;
        channelType?: string;
      }
    >({
      query(arg) {
        return {
          url: `hub/communication-v2/api/threads`,
          method: 'GET',
          params: arg,
          cache: 'no-cache',
        };
      },
    }),
    updateThreadState: builder.mutation<
      UpdateThreadStateResponse,
      {
        sellerWorkspaceId: string;
        isActive?: boolean;
        issueStatus?: IssueStatus;
        channelId: string;
        noChannelUpdate?: boolean;
      }
    >({
      query(arg) {
        return {
          url: `hub/communication-v2/api/threads`,
          method: 'PUT',
          body: arg,
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then((response) => {
            const { data } = response;
            const { id, issueStatus } = data;
            dispatch(
              upsertChannel({
                [id]: {
                  issueStatus,
                },
              })
            );
            if (request?.noChannelUpdate) {
              return;
            }
            dispatch(updateCurrentChannel(id));
          })
          .catch((error) => console.log(error));
      },
    }),
    createCustomerThread: builder.mutation<
      CreateCustomerThreadResonse[],
      CreateCustomerThreadRequest
    >({
      query(arg) {
        return {
          url: `customers/${arg.workspaceId}?doCreateCustomerThread=true`,
          method: 'POST',
          body: arg.body,
        };
      },
    }),
    createThread: builder.mutation<Thread, CreateThread>({
      query(arg) {
        return {
          url: `hub/communication-v2/api/threads/groupThread`,
          method: 'POST',
          body: arg,
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then((response) => {
            const { data: thread } = response;
            const { id, channelName, channelType, customerId } = thread;
            const type =
              channelType === 'C'
                ? ChannelTypeEnum.CUSTOMERTHREAD
                : ChannelTypeEnum.GROUPTHREAD;
            dispatch(
              upsertChannel({
                [id]: {
                  ...thread,
                  isActive: true,
                  type,
                  latestMessage: channelName,
                  latestMessageTimeToken: Date.now() + '0000',
                  customerId: customerId || undefined,
                },
              })
            );
            dispatch(updateThreadTabNavigationValue(type));
            dispatch(updateCurrentChannel(id));
          })
          .catch((error) => console.log(error));
      },
    }),
    getThreadFilters: builder.mutation<
      ThreadFilterResponse,
      ThreadFiltersRequest
    >({
      query(arg) {
        const { workspaceId, body, params } = arg;
        return {
          url: `hub/communication-v2/api/threads/filters/${workspaceId}`,
          method: 'POST',
          body: body,
          params: params,
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled, getState }) {
        const filterScreen = MobileSiftScreen.CUSTOMER_THREADS;
        queryFulfilled
          .then((response) => {
            const { data } = response;
            const appliedFilters = getMobileAppliedFilterByScreen(filterScreen)(
              getState() as any
            );
            const filter = getThreadFilterItems(
              data,
              filterScreen,
              appliedFilters
            );
            dispatch(
              updateMobileFiltersByScreen({
                screen: filterScreen,
                loading: false,
                filter,
              })
            );
          })
          .catch((error) => {
            console.log('thread filter error', error);
            dispatch(
              updateMobileFiltersByScreen({
                screen: filterScreen,
                loading: false,
                filter: {},
              })
            );
          });
      },
    }),
    getThreadsList: builder.mutation<ThreadsListResponse, ThreadsListRequest>({
      query(arg) {
        const { workspaceId, sellerWorkspaceId, body } = arg;
        return {
          url: `hub/communication-v2/api/threads/list/${workspaceId}`,
          method: 'POST',
          body: body,
          params: { sellerWorkspaceId },
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const channelType = request?.body?.filter?.channelType;
        const appliedStatus = request?.body?.filter?.status;
        const filterScreen =
          channelType === ChannelTypeEnum.ISSUETHREAD
            ? MobileSiftScreen.ISSUE_THREADS
            : MobileSiftScreen.CUSTOMER_THREADS;
        if (!isWeb())
          queryFulfilled
            .then((response) => {
              const data = response.data;
              if (data.statusFilter) {
                const statusFilter = handleStatusFilter(
                  data.statusFilter,
                  appliedStatus
                );
                dispatch(
                  updateMobileFiltersByScreen({
                    filter: {
                      sectionFilter: {
                        [SectionFilterKeys.Status]: {
                          multiSelect: true,
                          value: statusFilter,
                        },
                      },
                    },
                    screen: filterScreen,
                  })
                );
              }
            })
            .catch((error) => {
              console.log('thread list error', error);
            });
      },
      transformResponse: (response: any) => {
        if (isArray(response)) {
          return {
            channels: response as ThreadListChannel[],
            statusFilter: {},
          } as ThreadsListResponse;
        }
        return response as ThreadsListResponse;
      },
    }),
    addMessageReaction: builder.mutation<
      AddMessageReactionResponse,
      AddMessageReactionRequest
    >({
      query(arg) {
        return {
          url: `hub/communication-v2/api/message-actions`,
          method: 'POST',
          body: arg?.body,
          params: arg?.params,
        };
      },
    }),
    updateThreadLastVisitTime: builder.mutation<
      ThreadLastVisitResponseData[],
      UpdateThreadLastVisitRequest
    >({
      query(arg) {
        const { channels, sellerWorkspaceId } = arg;
        return {
          url: `hub/communication-v2/api/visit/set`,
          method: 'POST',
          params: { sellerWorkspaceId },
          body: { channelIds: channels },
        };
      },
    }),
    createGroupThread: builder.mutation<
      CreateGroupResponse,
      CreateGroupRequest
    >({
      query(args) {
        return {
          url: `hub/communication-v2/api/channels`,
          method: 'POST',
          params: args.params,
          body: args.body,
        };
      },
    }),
    updateGroupMembers: builder.mutation<
      UpdateGroupMembersResponse,
      UpdateGroupMembersRequest
    >({
      query(args) {
        return {
          url: `hub/communication-v2/api/channels/members`,
          method: 'POST',
          params: args.params,
          body: args.body,
        };
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useCreateIssueThreadMutation,
  useLazySearchThreadsQuery,
  useUpdateThreadStateMutation,
  useCreateCustomerThreadMutation,
  useCreateThreadMutation,
  useGetThreadFiltersMutation,
  useGetThreadsListMutation,
  useAddMessageReactionMutation,
  useUpdateThreadLastVisitTimeMutation,
  useCreateGroupThreadMutation,
  useUpdateGroupMembersMutation,
} = threadApi;
